import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';

function SpacetimePlane({ mass }: { mass: number }) {
  const geomRef = useRef<THREE.PlaneGeometry>(null);

  // Cache original flat vertices to compute distance-based displacement
  const originalPositions = useMemo(() => {
    const geom = new THREE.PlaneGeometry(20, 20, 40, 40);
    const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
    const array = new Float32Array(posAttr.array.length);
    array.set(posAttr.array);
    return array;
  }, []);

  // Deform grid vertices dynamically when mass changes
  useEffect(() => {
    const geom = geomRef.current;
    if (!geom) return;

    const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
    const arr = posAttr.array as Float32Array;

    for (let i = 0; i < posAttr.count; i++) {
      const x = originalPositions[i * 3];
      const y = originalPositions[i * 3 + 1];

      const dist = Math.sqrt(x * x + y * y);
      // displacement = -mass * 1.5 / (dist + 0.5)
      const displacement = (-mass * 1.5) / (dist + 0.5);

      arr[i * 3 + 2] = displacement;
    }
    posAttr.needsUpdate = true;
    geom.computeVertexNormals();
  }, [mass, originalPositions]);

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry ref={geomRef} args={[20, 20, 40, 40]} />
      <meshBasicMaterial color="#1D4ED8" wireframe transparent opacity={0.5} />
    </mesh>
  );
}

function CoronaParticles({ mass }: { mass: number }) {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.getElapsedTime() * 1.8;
      groupRef.current.rotation.x = state.clock.getElapsedTime() * 0.4;
    }
  });

  const particles = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => {
      const angle = (i * Math.PI * 2) / 15;
      const radius = 0.18 + Math.random() * 0.25;
      const yOffset = (Math.random() - 0.5) * 0.15;
      return { angle, radius, yOffset };
    });
  }, []);

  const sY = -mass * 3.0; // depth of the singularity at center

  return (
    <group ref={groupRef} position={[0, sY, 0]}>
      {particles.map((p, idx) => (
        <mesh key={idx} position={[Math.cos(p.angle) * p.radius, p.yOffset, Math.sin(p.angle) * p.radius]}>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshBasicMaterial color="#F97316" />
        </mesh>
      ))}
    </group>
  );
}

function EventHorizon({ mass }: { mass: number }) {
  const ehRadius = mass * 0.5;

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
      <torusGeometry args={[ehRadius, 0.035, 8, 64]} />
      <meshStandardMaterial color="#EF4444" emissive="#EF4444" emissiveIntensity={1.5} />
      
      {/* Event Horizon Label */}
      <Html distanceFactor={15} position={[ehRadius, 0.2, 0]}>
        <div className="bg-[#EF4444]/90 text-white font-bold text-[8px] px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap select-none pointer-events-none border border-red-400/20">
          Event Horizon
        </div>
      </Html>
    </mesh>
  );
}

function DoomedPhoton({ mass }: { mass: number }) {
  const photonRef = useRef<THREE.Mesh>(null);
  const [opacity, setOpacity] = useState<number>(1.0);

  // Spiral / linear infall path
  const angle = Math.PI / 5; // direction of infall

  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();
    const progress = (elapsed % 4.0) / 4.0; // 4s loop

    // Accelerating inward path: d goes from 9.5 to 0
    const d = 9.5 * (1.0 - Math.pow(progress, 2.2));
    
    // Follow the warped spacetime surface Y depth
    const y = (-mass * 1.5) / (d + 0.5);

    const ehRadius = mass * 0.5;
    const computedOpacity = d <= ehRadius ? Math.max(0, d / ehRadius) : 1.0;
    setOpacity(computedOpacity);

    if (photonRef.current) {
      photonRef.current.position.set(
        d * Math.cos(angle),
        y,
        d * Math.sin(angle)
      );
    }
  });

  return (
    <mesh ref={photonRef}>
      <sphereGeometry args={[0.08, 16, 16]} />
      <meshBasicMaterial color="#FBBF24" transparent opacity={opacity} />
      
      {/* Floating doomed photon label */}
      {opacity > 0.05 && (
        <Html distanceFactor={15} position={[0, 0.35, 0]}>
          <div className="bg-[#FBBF24]/90 text-slate-900 font-extrabold text-[8px] px-1.5 py-0.5 rounded shadow-md whitespace-nowrap select-none pointer-events-none border border-white/20">
            Photon (doomed)
          </div>
        </Html>
      )}
    </mesh>
  );
}

export function SpacetimeWarp() {
  const [mass, setMass] = useState<number>(1.5);

  const singularityY = -mass * 3.0;

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <Canvas camera={{ position: [0, 12, 15], fof: 45 } as any}>
        <color attach="background" args={['#0F172A']} />
        <ambientLight intensity={0.1} />
        
        {/* Blue Pointlight centered at singularity */}
        <pointLight position={[0, singularityY, 0]} color="#3B82F6" intensity={4.0} decay={1.5} distance={20} />

        <SpacetimePlane mass={mass} />
        <EventHorizon mass={mass} />
        <DoomedPhoton mass={mass} />
        <CoronaParticles mass={mass} />

        {/* Singularity core */}
        <mesh position={[0, singularityY, 0]}>
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshStandardMaterial color="#000000" emissive="#300000" roughness={0.1} />
        </mesh>

        <OrbitControls enableZoom={true} enablePan={false} maxPolarAngle={Math.PI / 2.1} />
      </Canvas>

      {/* Title Header Top Left */}
      <div className="absolute top-3 left-3 pointer-events-none select-none text-white/40 text-sm font-medium">
        Black Hole Spacetime Warping
      </div>

      {/* Slider Controls Bottom Center */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-1">
        <span className="text-white text-[10px] uppercase font-bold tracking-wider opacity-60 text-center">
          Black Hole Mass: {mass.toFixed(1)} Solar Masses (M☉)
        </span>
        <input
          type="range"
          min="0.5"
          max="5"
          step="0.1"
          value={mass}
          onChange={(e) => setMass(parseFloat(e.target.value))}
          className="w-full accent-cyan-400 cursor-pointer h-1.5 bg-white/20 rounded-lg appearance-none"
        />
      </div>
    </div>
  );
}
